import React from 'react'
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay } from 'date-fns'

type MonthCalendarProps = {
    monthDate: Date
    startDate: Date | null
    endDate: Date | null
    onDateSelect: (date: Date) => void
    minDate?: Date
    maxDate?: Date
    size?: 'md' | 'sm'
}

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const CALENDAR_SIZE_CONFIG = {
    md: {
        wrapper: 'w-1/2 p-2 h-[12.7rem]',
        title: 'text-[0.6rem]',
        weekDay: 'text-[0.5rem]',
        dayGrid: 'text-[0.55rem]',
        dayCell: 'h-5',
        dayBtn: 'w-5 h-5',
        emptySpan: 'w-5 h-5',
    },
    sm: {
        wrapper: 'w-1/2 p-2 h-[12.7rem]',
        title: 'text-[0.6rem]',
        weekDay: 'text-[0.5rem]',
        dayGrid: 'text-[0.55rem]',
        dayCell: 'h-5',
        dayBtn: 'w-5 h-5',
        emptySpan: 'w-5 h-5',
    },
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({ monthDate, startDate, endDate, onDateSelect, minDate, maxDate, size = 'md' }) => {
    const generateDays = (): (Date | null)[] => {
        const start = startOfMonth(monthDate)
        const end = endOfMonth(monthDate)
        const days = eachDayOfInterval({ start, end })

        // Adjust so Monday is first day of week
        const blanks = new Array((start.getDay() + 6) % 7).fill(null)

        return [...blanks, ...days]
    }

    const isInRange = (day: Date) => startDate !== null && endDate !== null && day > startDate && day < endDate

    const isDateDisabled = (day: Date): boolean => {
        if (minDate && day < minDate) return true
        if (maxDate && day > maxDate) return true
        return false
    }

    const getButtonClass = (day: Date): string => {
        if (startDate && isSameDay(day, startDate)) return 'bg-primary text-black'
        if (endDate && isSameDay(day, endDate)) return 'bg-primary text-black'
        if (isInRange(day)) return 'bg-slate-700'
        if (isDateDisabled(day)) return 'text-slate-500 cursor-not-allowed'
        return 'hover:bg-slate-700'
    }

    const days = generateDays()
    const sc = CALENDAR_SIZE_CONFIG[size]

    return (
        <div className={sc.wrapper}>
            <h3 className={`${sc.title} text-slate-100 font-medium text-center`}>{format(monthDate, 'MMMM yyyy')}</h3>
            <div className={`grid grid-cols-7 mt-2 ${sc.weekDay} text-center text-slate-400`}>
                {WEEK_DAYS.map((d) => (
                    <div key={d}>{d}</div>
                ))}
            </div>
            <div className={`grid grid-cols-7 text-slate-200 gap-1 mt-1 text-center ${sc.dayGrid}`}>
                {days.map((day, i) => (
                    <div key={day ? format(day, 'yyyy-MM-dd') : `empty-${i}`} className={sc.dayCell}>
                        {day ? (
                            <button type="button" onClick={() => onDateSelect(day)} disabled={isDateDisabled(day)} className={`${sc.dayBtn} rounded-full leading-none transition ${getButtonClass(day)}`}>
                                {format(day, 'd')}
                            </button>
                        ) : (
                            <span className={`inline-block ${sc.emptySpan}`} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default MonthCalendar
